/**
 * Utility functions for handling Swiss Post API CP850 encoding requirements
 *
 * CP850 (DOS Latin 1) is a character encoding that includes:
 * - Basic Latin alphabet (A-Z, a-z)
 * - Numbers (0-9)
 * - Common punctuation
 * - Western European accented characters (é, ü, ñ, etc.)
 * - Box drawing characters
 *
 * It does NOT support:
 * - Emojis
 * - Most Unicode symbols
 * - Asian characters
 * - Mathematical symbols not in the basic set
 */

// CP850 code page character ranges (simplified check)
// This is a practical check for common valid characters
const CP850_RANGES = [
  // Basic ASCII printable characters (space through ~)
  [0x20, 0x7e],
  // Extended ASCII characters that are part of CP850
  [0x80, 0xff],
]

/**
 * Check if a character is likely valid in CP850 encoding
 * This is a simplified check that covers most common cases
 */
function isValidCP850Char(char: string): boolean {
  const code = char.charCodeAt(0)

  // Allow newlines and carriage returns
  if (code === 0x0a || code === 0x0d) {
    return true
  }

  // Check if character code falls within CP850 ranges
  for (const [min, max] of CP850_RANGES) {
    if (min && max && code >= min && code <= max) {
      return true
    }
  }

  return false
}

/**
 * Detect invalid characters in text for CP850 encoding
 * Returns an array of invalid characters found
 */
export function detectInvalidCharacters(text: string): string[] {
  const invalid = new Set<string>()

  for (const char of text) {
    if (!isValidCP850Char(char)) {
      invalid.add(char)
    }
  }

  return Array.from(invalid)
}

/**
 * Check if text contains only valid CP850 characters
 */
export function isValidCP850Text(text: string): boolean {
  return detectInvalidCharacters(text).length === 0
}

/**
 * Sanitize text by removing invalid CP850 characters
 * Returns the sanitized text and a list of removed characters
 */
export function sanitizeForCP850(text: string): {
  sanitized: string
  removed: string[]
} {
  const removed = new Set<string>()
  let sanitized = ''

  for (const char of text) {
    if (isValidCP850Char(char)) {
      sanitized += char
    } else {
      removed.add(char)
    }
  }

  return {
    sanitized,
    removed: Array.from(removed),
  }
}
